import {
  Gauge,
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
} from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

import { formatTime } from './utils';
import { VideoSeekbar, type VideoChapter } from './video-seekbar';

export type { VideoChapter } from './video-seekbar';

export const SKIP_SECONDS = 15;
export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

interface VideoControlsProps {
  currentTime: number;
  duration: number;
  buffered: number;
  chapters: VideoChapter[];
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  playbackRate: number;
  showSpeedMenu: boolean;
  isFullscreen: boolean;
  showControls: boolean;
  progressBarRef: React.RefObject<HTMLButtonElement | null>;
  onProgressClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onProgressMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onPlayPause: () => void;
  onSkip: (delta: number) => void;
  onToggleMute: () => void;
  onVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRateChange: (rate: number) => void;
  onToggleSpeedMenu: () => void;
  onToggleFullscreen: () => void;
}

export const VideoControls = ({
  currentTime,
  duration,
  buffered,
  chapters,
  isPlaying,
  isMuted,
  volume,
  playbackRate,
  showSpeedMenu,
  isFullscreen,
  showControls,
  progressBarRef,
  onProgressClick,
  onProgressMouseDown,
  onPlayPause,
  onSkip,
  onToggleMute,
  onVolumeChange,
  onRateChange,
  onToggleSpeedMenu,
  onToggleFullscreen,
}: VideoControlsProps) => {
  return (
    <div
      className={cn(
        'absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2.5 md:p-6 transition-opacity duration-300',
        showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}
    >
      {/* Progress bar with buffered indicator + chapter markers */}
      <VideoSeekbar
        currentTime={currentTime}
        duration={duration}
        buffered={buffered}
        chapters={chapters}
        progressBarRef={progressBarRef}
        onProgressClick={onProgressClick}
        onProgressMouseDown={onProgressMouseDown}
      />

      {/* Control buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 md:space-x-4">
          {/* Rewind 15s */}
          <button
            onClick={() => onSkip(-SKIP_SECONDS)}
            className="text-white hover:text-blue-400 transition-colors relative"
            aria-label={`Rewind ${SKIP_SECONDS} seconds`}
            title={`Rewind ${SKIP_SECONDS} seconds`}
          >
            <RotateCcw className="h-5 w-5" />
            <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold">
              {SKIP_SECONDS}
            </span>
          </button>

          {/* Play/Pause */}
          <button
            onClick={onPlayPause}
            className="text-white hover:text-blue-400 transition-colors"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5" />
            )}
          </button>

          {/* Skip forward 15s */}
          <button
            onClick={() => onSkip(SKIP_SECONDS)}
            className="text-white hover:text-blue-400 transition-colors relative"
            aria-label={`Skip forward ${SKIP_SECONDS} seconds`}
            title={`Skip forward ${SKIP_SECONDS} seconds`}
          >
            <RotateCw className="h-5 w-5" />
            <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold">
              {SKIP_SECONDS}
            </span>
          </button>

          {/* Volume */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleMute}
              className="text-white hover:text-blue-400 transition-colors"
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-5 w-5" />
              ) : (
                <Volume2 className="h-5 w-5" />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={onVolumeChange}
              className="w-20 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>

          {/* Time */}
          <div className="text-white md:text-sm text-[10px] font-mono">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        <div className="flex items-center space-x-3 md:space-x-4">
          {/* Playback speed */}
          <div className="relative">
            <button
              onClick={onToggleSpeedMenu}
              className="flex items-center gap-1 text-white hover:text-blue-400 transition-colors"
              aria-label="Playback speed"
              aria-haspopup="menu"
              aria-expanded={showSpeedMenu}
              title="Playback speed"
            >
              <Gauge className="h-5 w-5" />
              <span className="text-xs font-semibold tabular-nums">
                {playbackRate}x
              </span>
            </button>
            {showSpeedMenu ? (
              <div
                className="absolute bottom-full right-0 mb-2 min-w-[4rem] rounded-lg bg-black/90 py-1 shadow-lg"
                role="menu"
              >
                {SPEED_OPTIONS.map(rate => (
                  <button
                    key={rate}
                    role="menuitemradio"
                    aria-checked={rate === playbackRate}
                    onClick={() => onRateChange(rate)}
                    className={cn(
                      'block w-full px-4 py-1.5 text-left text-xs hover:bg-white/15 transition-colors',
                      rate === playbackRate
                        ? 'text-blue-400 font-semibold'
                        : 'text-white'
                    )}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Fullscreen */}
          <button
            onClick={onToggleFullscreen}
            className="text-white hover:text-blue-400 transition-colors"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <Minimize className="h-5 w-5" />
            ) : (
              <Maximize className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
